import "./SuggestionInput.scss";
import {flattenPaths, getNestedValue} from "../../utils/Utils";
import {useRef, useState} from "react";
import {SuggestionList} from "../DocumentEditor/suggestionList/SuggestionList";
import Input from "../Input/Input";

const COMMANDS = [
    {label: "inline", value: "inline"},
    {label: "orderedList", value: "orderedList"},
    {label: "bulletList", value: "bulletList"},
];
const COMMAND_VALUES = COMMANDS.map(c => c.value);

const useVariableSuggestions = (variables, query) => {
    if (!query?.includes("@")) return [];

    const search = query.split("@").pop();

    if (!search) return COMMANDS;

    const commandMatch = COMMANDS.find(c => search.includes(`.${c.value}.`));
    if (commandMatch) {
        const [listPath, subSearch] = search.split(`.${commandMatch.value}.`);
        const list = getNestedValue(variables, listPath);

        if (Array.isArray(list) && list.length && typeof list[0] === "object") {
            return Object.keys(list[0])
                .filter(key => Array.isArray(list[0][key]))
                .filter(key => key.toLowerCase().includes((subSearch ?? "").toLowerCase()))
                .map(key => ({
                    label: `${listPath}.${commandMatch.value}.${key}`,
                    value: `${listPath}.${commandMatch.value}.${key}`,
                }));
        }
    }

    // wait for dot after command
    if (COMMANDS.find(c => search.endsWith(`.${c.value}`))) return [];

    // show commands and fields after list path
    if (search.endsWith(".")) {
        const listPath = search.slice(0, -1);
        const val = getNestedValue(variables, listPath);
        if (Array.isArray(val)) {
            return COMMANDS.map(c => ({
                label: `${listPath}.${c.value}`,
                value: `${listPath}.${c.value}`,
            }));
        }
    }

    // partial field search inside list
    const parts = search.split(".");
    const possibleListPath = parts.slice(0, -1).join(".");
    const fieldSearch = parts[parts.length - 1];
    const possibleList = getNestedValue(variables, possibleListPath);

    if (Array.isArray(possibleList) && possibleList.length && typeof possibleList[0] === "object") {
        const commandSuggestions = COMMANDS
            .filter(c => c.label.toLowerCase().includes(fieldSearch.toLowerCase()))
            .map(c => ({label: `${possibleListPath}.${c.value}`, value: `${possibleListPath}.${c.value}`}));

        const fieldSuggestions = Object.keys(possibleList[0])
            .filter(key => key.toLowerCase().includes(fieldSearch.toLowerCase()))
            .map(key => ({label: `${possibleListPath}.${key}`, value: `${possibleListPath}.${key}`}));

        return [...commandSuggestions, ...fieldSuggestions];
    }

    const matchedCommand = COMMANDS.find(c => search.startsWith(`${c.value}.`));
    if (matchedCommand) {
        const subSearch = search.slice(matchedCommand.value.length + 1);
        return flattenPaths(variables)
            .filter(path => {
                const val = getNestedValue(variables, path);
                return Array.isArray(val) && !(val.length > 0 && typeof val[0] === "object");
            })
            .filter(path => path.toLowerCase().includes(subSearch.toLowerCase()))
            .map(path => ({label: `${matchedCommand.value}.${path}`, value: `${matchedCommand.value}.${path}`}));
    }

    const matchingCommands = COMMANDS.filter(c =>
        c.label.toLowerCase().includes(search.toLowerCase())
    );

    const matchingScalars = flattenPaths(variables)
        .filter(path => !Array.isArray(getNestedValue(variables, path)))
        .filter(path => path.toLowerCase().includes(search.toLowerCase()))
        .map(path => ({label: path, value: path}));

    const matchingLists = flattenPaths(variables)
        .filter(path => {
            const val = getNestedValue(variables, path);
            return Array.isArray(val) && val.length > 0 && typeof val[0] === "object";
        })
        .filter(path => path.toLowerCase().includes(search.toLowerCase()))
        .map(path => ({label: path, value: path}));

    return [...matchingCommands, ...matchingScalars, ...matchingLists];
};

const SuggestionInput = ({value, onChange, variables}) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestions = useVariableSuggestions(variables, value);
    const suggestionRef = useRef(null);

    const handleCommand = (item) => {
        const before = value.split("@").slice(0, -1).join("@");
        const val = getNestedValue(variables, item.value);
        const isObjectList = Array.isArray(val) && val.length && typeof val[0] === "object";
        const isCommand = COMMAND_VALUES.some(c => item.value.endsWith(c));

        if (isObjectList || isCommand) {
            onChange({target: {value: `${before}@${item.value}.`}});
            return;
        }

        onChange({target: {value: `${before}@${item.value}`}});
        setShowSuggestions(false);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) return;
        if (["ArrowUp", "ArrowDown", "Enter"].includes(e.key)) {
            e.preventDefault();
            suggestionRef.current?.onKeyDown(e);
        }
        if (e.key === "Escape") setShowSuggestions(false);
    }

    return (
        <div style={{position: "relative"}}>
            <Input
                layout="vertical"
                value={value}
                handleChange={e => {
                    onChange(e);
                    setShowSuggestions(e.target.value.includes("@"));
                }}
                onKeyDown={handleKeyDown}
            />
            {showSuggestions && !!suggestions.length && (
                <div onMouseDown={e => e.preventDefault()}>
                    <SuggestionList
                        className="suggestion-list-table"
                        ref={suggestionRef}
                        items={suggestions}
                        command={handleCommand}
                    />
                </div>
            )}
        </div>
    );
};

export default SuggestionInput;